/* eslint-disable react-hooks/exhaustive-deps */
import Button from "@lhd/components/Button";
import SafeHtml from "@lhd/components/SafeHtml";
import DateHierarchyFilter, {
  formatRawDateToAPI,
} from "@lhd/components/DateTree";
import { dateTypeColumns } from "@lhd/features/Customers/CustomerSheet/utils";
import { customer_sheet_fields_convert_attr } from "@lhd/features/Customers/ManagementCustomerSheet/ManagementUserSheetList/utility";
import { task_sheet_fields_convert_attr } from "@lhd/features/ProjectManagement/ManagementTaskSheet/ManagementUserTaskList/utility";
import { project_sheet_fields_convert_attr } from "@lhd/features/ProjectManagement/ProjectInformation/ManagementProjectListSheet/ManagementUserTaskList/utility";
import { arrangment_special_fields_convert_attr } from "@lhd/features/Salary/Arrangements/ArrangementsList/utility";
import { deduction_special_fields_convert_attr } from "@lhd/features/Salary/Deductions/DeductionsList/utility";
import { expense_special_fields_convert_attr } from "@lhd/features/Salary/Expenses/ExpensesList/utility";
import { provision_sheet_special_field_convert_attr } from "@lhd/features/Salary/Provisions/ProvisionsList/utility";
import { staff_tax_sheet_special_field_convert_attr } from "@lhd/features/Salary/StaffTaxSheets/StaffTaxSheetsList/utility";
import { transfer_special_fields_convert_attr } from "@lhd/features/Salary/Transfers/TransfersList/utility";
import { work_special_fields_convert_attr } from "@lhd/features/Salary/Works/WorksList/utility";
import { toHalfWidth } from "@lhd/features/Salary/SalarySheets/components/exportPDF/utility";
import { handleDataFilter } from "@lhd/utility";
import { Checkbox, Input, Radio, Spin } from "antd";
import { get, isEmpty, unionBy, uniq, set } from "lodash";
import { clearSortFromOtherColumns } from "@lhd/utility";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { BsSearch } from "react-icons/bs";
import { FilterStyled } from "../../style";
import { DATE_FIELDS } from "@lhd/features/Customers/NewCustomerControl/utils";
import {
  optionFilterCellEmpty,
  parseOptions,
  matchesDynamicDatePattern,
} from "../../utils";

const DateTree = [
  "birthday",
  "retired_date",
  "join_date",
  "employment_insurance_lost_date",
  "employment_insurance_get_date",
  "transfered_at",
  "health_insurance_lost_date",
  "health_insurance_get_date",
  "start_at",
  "end_at",
  "achieve_start_at",
  "achieve_end_at",
  "contract_start_date",
  "onboarding_start_date",
  "industry_category_apply_date",
  "name_start_date",
  "name_history.start_date",
  "qualifications.start_date",
  "qualifications.end_date",
  "education_work.start_date",
  "education_work.end_date",
  "job_details.start_date",
  "job_details.end_date",
  "onboarding_start_date",
  "resignation_date",
  "regular_insurance.insurance_get_date",
  "regular_insurance.insurance_lost_date",
  "unemployment_insurance.insurance_get_date",
  "unemployment_insurance.insurance_lost_date",
  "employment.start_date",
  "worker_dependants.birthday",
  "death_date",
  "worker_org_chart_node_histories.date_version",
  ...dateTypeColumns,
  ...DATE_FIELDS,
  "employment.end_date",
];

const isDateTreeField = (columnId: string): boolean => {
  if (DateTree.includes(columnId)) {
    return true;
  }

  // Check for dynamic patterns
  return matchesDynamicDatePattern(columnId);
};

export const getActiveFiltersWithIndex = (obj: any, prefix = ""): any[] => {
  if (!obj || typeof obj !== "object") return [];
  if ("index" in obj) {
    return [{ key: prefix, value: obj }];
  }
  let result: any[] = [];
  for (const [k, v] of Object.entries(obj)) {
    result = result.concat(
      getActiveFiltersWithIndex(v, prefix ? `${prefix}.${k}` : k),
    );
  }
  return result;
};

interface FilterToolProps {
  columns?: any[];
  formik: any;
  additionalField?: string[];
  originalData?: any[];
  data?: any[];
  dateFieateHierarchyFields?: string[];
  filterFields?: string[];
  onFilter: () => void;
  getValue?: (value: string, type: string) => string;
  isPreferUseGetValue?: boolean;
  filterInfo: FilterInfoType;
  setFilterInfo?: (info: FilterInfoType) => void;
  fetchFilterOptions?: (columnId: string) => Promise<{
    options: { value: string; label: string }[];
    isShowEmptyDate: boolean;
  }>;
  apiFilterOptions?: any[];
  isFetchingFilterOptions?: boolean;
  isShowEmptyDate?: boolean;
  isApiFormat?: boolean;
  hideArrayOptions?: boolean;
}

type FilterInfoType = {
  open: boolean;
  columnId: string;
  position?: { x?: number; y?: number };
};

const FilterTool: FC<FilterToolProps> = ({
  columns,
  formik,
  additionalField = [],
  originalData,
  data,
  onFilter,
  filterInfo,
  setFilterInfo,
  dateFieateHierarchyFields,
  filterFields,
  getValue,
  isPreferUseGetValue,
  fetchFilterOptions,
  apiFilterOptions,
  isFetchingFilterOptions,
  isShowEmptyDate: isShowEmptyDateProp,
  isApiFormat = false,
  hideArrayOptions = false,
}) => {
  const [filterOptions, setFilterOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const prevFilterInfoRef = useRef({ open: false, columnId: "" });
  const prevApiFilterOptionsRef = useRef<any[] | undefined>(undefined);
  const [isShowEmptyDate, setIsShowEmptyDate] = useState<boolean>(false);
  const [localFilterValue, setLocalFilterValue] = useState<{
    search?: string;
    sort?: string;
    filter?: any[];
    filter_special_empty?: boolean;
  }>({});
  const [shouldFilter, setShouldFilter] = useState(false);
  const orginalColumnId = filterInfo.columnId?.includes("_attr")
    ? filterInfo.columnId.replace("_attr", "")
    : filterInfo.columnId;

  const activeColumn = useMemo(() => {
    return columns?.find(
      (c: any) =>
        c.columnId === filterInfo.columnId || c.columnId === orginalColumnId,
    );
  }, [columns, filterInfo.columnId, orginalColumnId]);

  const appPartType = (
    activeColumn?.appPart?.app_part_type || activeColumn?.type
  )?.toLowerCase();
  const isDateTreeFilter =
    isDateTreeField(filterInfo.columnId) ||
    [
      "date",
      "date_and_time",
      "time",
      "created_datetime",
      "updated_datetime",
    ].includes(appPartType);
  const treeMode = [
    "date",
    "date_and_time",
    "time",
    "created_datetime",
    "updated_datetime",
  ].includes(appPartType)
    ? appPartType
    : "default";

  const parseColumnActive = useMemo(() => {
    const salaryFieldsConvert = [
      ...staff_tax_sheet_special_field_convert_attr,
      ...provision_sheet_special_field_convert_attr,
      ...expense_special_fields_convert_attr,
      ...transfer_special_fields_convert_attr,
      ...work_special_fields_convert_attr,
      ...arrangment_special_fields_convert_attr,
      ...deduction_special_fields_convert_attr,
      ...task_sheet_fields_convert_attr,
      ...project_sheet_fields_convert_attr,
      ...customer_sheet_fields_convert_attr,
    ];

    return [...uniq(salaryFieldsConvert), ...additionalField].includes(
      filterInfo?.columnId,
    )
      ? `${filterInfo?.columnId}_attr`
      : filterInfo?.columnId;
  }, [filterInfo?.columnId, additionalField]);

  const isSelectAll = useMemo(() => {
    if (isEmpty(filterOptions)) {
      if (isShowEmptyDate) {
        return !!localFilterValue?.filter_special_empty;
      }
      return false;
    }
    const isAllChecked =
      filterOptions.length > 0 &&
      localFilterValue?.filter?.length === filterOptions.length;
    const isEmptyChecked = isShowEmptyDate
      ? !!localFilterValue?.filter_special_empty
      : true;
    return isAllChecked && isEmptyChecked;
  }, [localFilterValue, filterOptions, isShowEmptyDate]);

  // Detect when user uncheck all options (including empty option)
  const isUncheckAll =
    !isEmpty(localFilterValue) &&
    localFilterValue?.filter?.length === 0 &&
    !localFilterValue?.filter_special_empty;

  const isResetForm =
    isEmpty(localFilterValue) ||
    ((!localFilterValue?.search ||
      [null, ""].includes(localFilterValue?.search)) &&
      (!localFilterValue?.sort || localFilterValue?.sort === "") &&
      (localFilterValue?.filter === undefined || isSelectAll));

  const handleCancel = () => {
    setFilterInfo &&
      setFilterInfo({
        open: false,
        columnId: "",
      });
  };

  const handleFilter = async () => {
    const currentValue = get(formik.values, parseColumnActive, {});
    const oldIndex = currentValue?.index;

    // Check if values actually changed to avoid unnecessary clearing of downstream filters
    const isLocalFilterEmpty =
      (!localFilterValue?.search ||
        [null, ""].includes(localFilterValue?.search)) &&
      (!localFilterValue?.sort || localFilterValue?.sort === "") &&
      (localFilterValue?.filter === undefined || isSelectAll);

    const isFilterConditionChanged =
      currentValue?.search !== localFilterValue?.search ||
      currentValue?.filter_special_empty !==
        localFilterValue?.filter_special_empty ||
      JSON.stringify([...(currentValue?.filter || [])].sort()) !==
        JSON.stringify([...(localFilterValue?.filter || [])].sort());

    const shouldTreatAsReset = isResetForm || isLocalFilterEmpty;

    const cleanedFilterValue = {
      ...localFilterValue,
      filter: isSelectAll ? undefined : localFilterValue?.filter,
      filter_special_empty: isSelectAll
        ? undefined
        : localFilterValue?.filter_special_empty,
    };

    // If setting a new sort, clear sort from other columns
    if (cleanedFilterValue?.sort && cleanedFilterValue.sort !== "") {
      const clearedValues = clearSortFromOtherColumns(
        formik.values,
        parseColumnActive,
      );

      // Explicitly clear all columns that had sort removed (including sort-only columns)
      Object.keys(formik.values).forEach((fieldKey) => {
        if (fieldKey !== parseColumnActive && !(fieldKey in clearedValues)) {
          // Column was removed by clearSortFromOtherColumns (had only sort)
          formik.setFieldValue(fieldKey, {});
        }
      });

      // Update columns that still have filter/search/filter_special_empty
      Object.keys(clearedValues).forEach((fieldKey) => {
        if (fieldKey !== parseColumnActive) {
          formik.setFieldValue(fieldKey, clearedValues[fieldKey]);
        }
      });
    }

    // When user uncheck all options (including empty option), keep filter = [] to show empty data
    // Don't treat as reset form
    if (shouldTreatAsReset && !isUncheckAll) {
      await formik.setFieldValue(parseColumnActive, {});
    } else {
      if (oldIndex === undefined) {
        const allActive = getActiveFiltersWithIndex(formik.values);
        const maxIndex = Math.max(0, ...allActive.map((f) => f.value.index));
        await formik.setFieldValue(parseColumnActive, {
          ...cleanedFilterValue,
          index: maxIndex + 1,
        });
      } else {
        await formik.setFieldValue(parseColumnActive, {
          ...cleanedFilterValue,
          index: oldIndex,
        });
      }
    }

    // Clear downstream filters if this filter was previously active and just changed
    if (
      isFilterConditionChanged &&
      oldIndex !== undefined &&
      oldIndex !== null
    ) {
      const allActive = getActiveFiltersWithIndex(formik.values);
      const downstreamKeys = allActive
        .filter((f) => f.value.index > oldIndex)
        .map((f) => f.key);

      for (const key of downstreamKeys) {
        await formik.setFieldValue(key, {});
      }
    }

    setShouldFilter(true);
    handleCancel();
  };

  useEffect(() => {
    if (shouldFilter) {
      onFilter();
      setShouldFilter(false);
    }
  }, [formik.values, shouldFilter, onFilter]);

  useEffect(() => {
    const isNewFilterOpen =
      filterInfo.open &&
      (filterInfo.columnId !== prevFilterInfoRef.current.columnId ||
        !prevFilterInfoRef.current.open);

    if (
      !isNewFilterOpen &&
      prevApiFilterOptionsRef.current === apiFilterOptions
    )
      return;

    prevFilterInfoRef.current = {
      open: filterInfo.open,
      columnId: filterInfo.columnId,
    };
    prevApiFilterOptionsRef.current = apiFilterOptions;

    const loadOptions = async () => {
      const currentFormikValue = get(formik.values, parseColumnActive);
      const columnId = filterInfo.columnId;
      const filterValues = formik.values;
      const currentFilterValue = get(filterValues, parseColumnActive);
      let options: any = [];
      let showEmptyDate = false;

      if (apiFilterOptions) {
        options = apiFilterOptions
          .map((opt: any) => {
            if (!opt || typeof opt !== "object") return null;
            const innerOpt =
              opt.label !== undefined ? opt : (Object.values(opt)[0] as any);
            if (!innerOpt || innerOpt.value === undefined) return null;
            return {
              value: innerOpt.value,
              label: innerOpt.label ?? innerOpt.value,
            };
          })
          .filter(Boolean);
        showEmptyDate = isShowEmptyDateProp || false;
      } else if (fetchFilterOptions) {
        setIsLoadingOptions(true);
        try {
          const res = await fetchFilterOptions(columnId);
          options = res.options;
          showEmptyDate = res.isShowEmptyDate;
        } finally {
          setIsLoadingOptions(false);
        }
      } else {
        if (
          (isEmpty(currentFilterValue) && isEmpty(filterValues)) ||
          currentFilterValue?.index === 1
        ) {
          const { options: parsedOptions, isShowEmptyDate: showEmptyDateFlag } =
            parseOptions(
              originalData || [],
              columnId,
              dateFieateHierarchyFields,
              getValue,
              isPreferUseGetValue,
            );

          options = parsedOptions;
          showEmptyDate = showEmptyDateFlag;
        } else if (!isEmpty(filterValues) && isEmpty(currentFilterValue)) {
          const currentData =
            data ||
            handleDataFilter(filterValues, originalData as any[], filterFields);
          const { options: parsedOptions, isShowEmptyDate: showEmptyDateFlag } =
            parseOptions(
              currentData || [],
              columnId,
              dateFieateHierarchyFields,
              getValue,
              isPreferUseGetValue,
            );

          options = parsedOptions;
          showEmptyDate = showEmptyDateFlag;
        } else {
          const activeFilters = getActiveFiltersWithIndex(filterValues);

          const newFilterValues: any = {};
          activeFilters
            .filter(
              (f) =>
                f.value.index <
                (currentFilterValue?.index !== undefined
                  ? currentFilterValue.index
                  : Infinity),
            )
            .sort((a, b) => a.value.index - b.value.index)
            .forEach((f) => {
              set(newFilterValues, f.key, f.value);
            });

          const newData: any[] = handleDataFilter(
            newFilterValues,
            originalData as any[],
            filterFields,
          );

          const { options: parsedOptions, isShowEmptyDate: showEmptyDateFlag } =
            parseOptions(
              newData,
              columnId,
              dateFieateHierarchyFields,
              getValue,
              isPreferUseGetValue,
            );
          options = parsedOptions;
          showEmptyDate = showEmptyDateFlag;
        }
      }

      const filterOptionsCustom = unionBy(options, (item: any) => item.value);

      const newFilterOptions = hideArrayOptions
        ? filterOptionsCustom.filter((item: any) => !Array.isArray(item.value))
        : filterOptionsCustom;

      const isFilterUndefined = currentFormikValue?.filter === undefined;
      const allValues = newFilterOptions.map((item: any) =>
        isDateTreeFilter && isApiFormat
          ? formatRawDateToAPI(item.value, treeMode) || item.value
          : item.value,
      );

      let extractedFilter = currentFormikValue?.filter || [];
      if (
        !Array.isArray(extractedFilter) &&
        typeof extractedFilter === "object" &&
        extractedFilter !== null
      ) {
        extractedFilter = Object.keys(extractedFilter).filter(
          (k) => extractedFilter[k] === true,
        );
      }

      setLocalFilterValue({
        search:
          typeof currentFormikValue?.search === "string"
            ? currentFormikValue.search
            : "",
        sort:
          typeof currentFormikValue?.sort === "string"
            ? currentFormikValue.sort
            : "",
        filter: isFilterUndefined ? allValues : extractedFilter,
        filter_special_empty: isFilterUndefined
          ? showEmptyDate
          : currentFormikValue?.filter_special_empty || false,
      });

      setIsShowEmptyDate(showEmptyDate);
      setFilterOptions(newFilterOptions);
    };

    loadOptions();
  }, [
    filterInfo.open,
    filterInfo.columnId,
    originalData,
    data,
    dateFieateHierarchyFields,
    filterFields,
    fetchFilterOptions,
    getValue,
    isPreferUseGetValue,
    apiFilterOptions,
  ]);

  return ReactDOM.createPortal(
    <FilterStyled className="filter-wrapper">
      <div
        className={`filter filter_container ${
          filterInfo.open ? "open" : "closed"
        }`}
        style={{
          left: `${Number(filterInfo.position?.x) - 20}px`,
          top: `${Number(filterInfo.position?.y) - 4}px`,
        }}
      >
        <div className="filter-wrapper">
          <div className="sort">
            <p className="title">ソート</p>
            <Radio.Group
              value={localFilterValue?.sort || ""}
              onChange={(e) => {
                setLocalFilterValue({
                  ...localFilterValue,
                  sort: e.target.value,
                });
              }}
              defaultValue="asc"
              options={[
                { label: "なし", value: "" },
                { label: "昇順 (A->Z)", value: "asc" },
                { label: "降順 (Z->A)", value: "desc" },
              ]}
            />
          </div>
          <div className="search">
            <p className="title">検索</p>
            <Input
              suffix={<BsSearch />}
              name="search-temp"
              className="input-filter"
              value={localFilterValue?.search || ""}
              onChange={(e) => {
                setLocalFilterValue({
                  ...localFilterValue,
                  search: e?.target?.value || "",
                });
              }}
              onPressEnter={handleFilter}
              onBlur={(e) => {
                const halfWidthValue = toHalfWidth(e?.target?.value || "");
                setLocalFilterValue({
                  ...localFilterValue,
                  search: halfWidthValue,
                });
              }}
            />
          </div>
          <hr />
          <div className="filter">
            <p className="title">フィルター</p>
            <Spin spinning={!!(isLoadingOptions || isFetchingFilterOptions)}>
              {isDateTreeFilter ? (
                <>
                  {isShowEmptyDate && (
                    <Checkbox
                      checked={localFilterValue?.filter_special_empty}
                      onChange={(e) =>
                        setLocalFilterValue({
                          ...localFilterValue,
                          filter_special_empty: e.target.checked,
                        })
                      }
                    >
                      {optionFilterCellEmpty.label}
                    </Checkbox>
                  )}

                  {!isEmpty(filterOptions) ? (
                    <DateHierarchyFilter
                      mode={treeMode}
                      timestamps={filterOptions.map((item) => item.value)}
                      initialChecked={localFilterValue?.filter || []}
                      onChange={(e, originalValues, apiKeys) => {
                        const isAppRecords = /^\/apps\/\d+\/records/.test(
                          window.location.pathname,
                        );
                        const isCreateWorker = /^\/workers\/create/.test(
                          window.location.pathname,
                        );

                        setLocalFilterValue({
                          ...localFilterValue,
                          filter: isAppRecords ? apiKeys || e : e,
                        });
                      }}
                    />
                  ) : (
                    <p className="title empty">空</p>
                  )}
                </>
              ) : (
                <>
                  {isShowEmptyDate && (
                    <Checkbox
                      checked={localFilterValue?.filter_special_empty}
                      onChange={(e) =>
                        setLocalFilterValue({
                          ...localFilterValue,
                          filter_special_empty: e.target.checked,
                        })
                      }
                    >
                      {optionFilterCellEmpty.label}
                    </Checkbox>
                  )}
                  {!isEmpty(filterOptions) ? (
                    <Checkbox.Group
                      value={localFilterValue?.filter || []}
                      style={{ width: "100%" }}
                      onChange={(e) => {
                        setLocalFilterValue({
                          ...localFilterValue,
                          filter: e,
                        });
                      }}
                    >
                      <div
                        className={`filter-options ${
                          filterOptions.length >= 6 ? "scroll" : ""
                        }`}
                        style={{
                          height: isEmpty(filterOptions)
                            ? 0
                            : filterOptions.length >= 6
                            ? 130
                            : filterOptions.length * 23,
                        }}
                      >
                        {filterOptions.map((item, idx: number) => (
                          <div key={idx} className="filter-item">
                            <Checkbox value={item.value}>
                              <SafeHtml
                                className="checkbox-label"
                                html={
                                  typeof item.label === "string"
                                    ? item.label
                                    : String(item.label ?? "")
                                }
                              />
                            </Checkbox>
                          </div>
                        ))}
                      </div>
                    </Checkbox.Group>
                  ) : !isShowEmptyDate ? (
                    <p className="title empty">空</p>
                  ) : null}
                </>
              )}
            </Spin>
          </div>
          <div className="action">
            <p
              onClick={(e) => {
                setLocalFilterValue({
                  ...localFilterValue,
                  filter: filterOptions.map((item) =>
                    isDateTreeFilter && isApiFormat
                      ? formatRawDateToAPI(item.value, treeMode) || item.value
                      : item.value,
                  ),
                  filter_special_empty: isShowEmptyDate,
                });
              }}
            >
              すべて選択
            </p>
            <span>-</span>
            <p
              onClick={(e) => {
                setLocalFilterValue({
                  ...localFilterValue,
                  filter: [],
                  filter_special_empty: false,
                });
              }}
            >
              クリア
            </p>
          </div>
          <div className="footer">
            <Button
              className="btn btn-filter"
              label="保存"
              onClick={handleFilter}
            />
            <Button
              className="btn btn-cancel"
              type="cancel-btn"
              label="キャンセル"
              onClick={handleCancel}
            />
          </div>
        </div>
      </div>
    </FilterStyled>,
    document.body,
  );
};

export default FilterTool;
